import React, { useEffect, useState } from 'react'
import { Button, Tooltip } from 'antd'
import { BodyText } from 'src/branding/typography'
import styled from 'styled-components/macro'
import { postGrowthEngineSuccessHubBusinessDays } from 'src/utils/growthEngineApi'
import ShareWithGroupButton from 'src/screens/menu-builder/components/ShareWithGroupButton'
import {
  SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS,
  SUCCESS_HUB_ISO_WEEKDAYS,
  SUCCESS_HUB_WEEKDAY_SHORT,
  formatSuccessHubOrderingSuggestion,
  formatSuccessHubWeekdayList,
  normalizeSuccessHubWeekdays,
  successHubWeekdaysEqual,
} from 'src/utils/growthEngineSuccessHub'

/**
 * Kitchen ordering days — Settings keeps the full ask; Progress uses quiet
 * compact (tucked under streak, outline actions only — never lime primary).
 *
 * @param {object} props
 * @param {string} props.venueId
 * @param {object|null} props.businessDays
 * @param {(next: object|null) => void} [props.onBusinessDaysChange]
 * @param {boolean} [props.compact] — Progress: collapsed until Change days
 * @param {boolean} [props.quiet] — Progress: no lime / no primary CTAs
 * @param {boolean} [props.embedded] — nest under header card (no outer shell)
 * @param {string} [props.testIdPrefix]
 */
const SuccessHubBusinessDaysCard = ({
  venueId,
  businessDays,
  onBusinessDaysChange,
  compact = false,
  quiet = false,
  embedded = false,
  testIdPrefix = 'settings',
}) => {
  const [busy, setBusy] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [editing, setEditing] = useState(false)

  const confirmed = Boolean(businessDays?.confirmed)
  const optedOut = Boolean(businessDays?.optedOut) && !confirmed
  const effectiveSource = businessDays?.effectiveWeekdays?.length
    ? businessDays.effectiveWeekdays
    : SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS
  const defaultWeekdaysKey =
    normalizeSuccessHubWeekdays(effectiveSource).join(',')

  useEffect(() => {
    const defaults = normalizeSuccessHubWeekdays(
      defaultWeekdaysKey
        ? defaultWeekdaysKey.split(',').map(Number)
        : SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS,
    )
    setSelectedDays(
      confirmed
        ? normalizeSuccessHubWeekdays(businessDays?.weekdays)
        : optedOut
        ? []
        : defaults,
    )
  }, [confirmed, optedOut, businessDays?.weekdays, defaultWeekdaysKey])

  useEffect(() => {
    if (!confirmed && !quiet) setEditing(false)
  }, [confirmed, quiet])

  if (!venueId || !businessDays) return null

  const confirmedWeekdays = normalizeSuccessHubWeekdays(businessDays?.weekdays)
  const defaultWeekdays = normalizeSuccessHubWeekdays(
    defaultWeekdaysKey
      ? defaultWeekdaysKey.split(',').map(Number)
      : SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS,
  )
  const effectiveList = formatSuccessHubWeekdayList(
    confirmed ? confirmedWeekdays : defaultWeekdays,
  )

  const showSummary = compact && !editing
  const suggested = normalizeSuccessHubWeekdays(businessDays?.suggestedWeekdays)
  const suggestionCopy = formatSuccessHubOrderingSuggestion(businessDays)
  const daysDirty = !successHubWeekdaysEqual(
    selectedDays,
    confirmed ? businessDays?.weekdays : optedOut ? [] : defaultWeekdays,
  )
  const canConfirmSelected =
    selectedDays.length > 0 && (!confirmed || daysDirty)
  const showUseSuggestion =
    suggested.length > 0 && !successHubWeekdaysEqual(selectedDays, suggested)

  const saveDays = async weekdays => {
    if (!venueId || busy) return
    setBusy(true)
    try {
      const next = await postGrowthEngineSuccessHubBusinessDays(
        venueId,
        weekdays,
      )
      const days = next.businessDays || null
      onBusinessDaysChange?.(days)
      const nextConfirmed = Boolean(days?.confirmed)
      const nextOptedOut = Boolean(days?.optedOut) && !nextConfirmed
      setSelectedDays(
        nextConfirmed
          ? normalizeSuccessHubWeekdays(days.weekdays)
          : nextOptedOut
          ? []
          : normalizeSuccessHubWeekdays(
              days?.effectiveWeekdays?.length
                ? days.effectiveWeekdays
                : SUCCESS_HUB_DEFAULT_ORDERING_WEEKDAYS,
            ),
      )
      if (compact) setEditing(false)
    } catch (err) {
      console.warn('Kitchen days failed to save', err)
    }
    setBusy(false)
  }

  const toggleDay = day => {
    setSelectedDays(current =>
      current.includes(day)
        ? current.filter(item => item !== day)
        : normalizeSuccessHubWeekdays([...current, day]),
    )
  }

  const cancelEdit = () => {
    setSelectedDays(
      confirmed ? confirmedWeekdays : optedOut ? [] : defaultWeekdays,
    )
    setEditing(false)
  }

  if (showSummary) {
    const summaryCopy = optedOut
      ? 'Streaks paused — confirm ordering days to count again.'
      : confirmed && effectiveList
      ? `Streaks count on ${effectiveList}.`
      : effectiveList
      ? `Counting ${effectiveList} by default.`
      : 'Streak settings'
    return (
      <Summary
        data-testid={`${testIdPrefix}-business-days`}
        data-mode='summary'
        $embedded={embedded}
        $quiet={quiet}
      >
        <Tooltip
          title='Ordering streaks only grow on these days. A quiet day you didn’t set never breaks the run.'
          mouseEnterDelay={0.25}
        >
          <SummaryCopy data-testid={`${testIdPrefix}-business-days-summary`}>
            {quiet ? (
              <>
                <SettingsLabel>Streak settings</SettingsLabel>
                {summaryCopy}
              </>
            ) : (
              summaryCopy
            )}
          </SummaryCopy>
        </Tooltip>
        <QuietLink
          type='link'
          onClick={() => setEditing(true)}
          data-testid={`${testIdPrefix}-business-days-change`}
          data-chrome='quiet'
        >
          Change days
        </QuietLink>
        <ShareWithGroupButton
          offer={{ kind: 'businessDays', venueId }}
          label='Share days with group…'
          component={QuietLink}
          data-chrome='quiet'
          data-testid={`${testIdPrefix}-business-days-share`}
        />
      </Summary>
    )
  }

  const suggestionList = formatSuccessHubWeekdayList(suggested)
  const useSuggestionLabel = suggestionList
    ? `Use ${suggestionList}`
    : 'Use suggested days'

  const bodyCopy = optedOut
    ? 'Streaks are paused. Tap the days this kitchen orders, then Confirm once — a quiet Sunday never breaks the run.'
    : confirmed
    ? 'Tap the days this kitchen orders, then Save. A quiet Sunday never breaks the run.'
    : 'Streaks already count Mon–Fri by default. Tap the days this kitchen orders (or Use the suggestion below), then Confirm once — a quiet Sunday never breaks the run.'

  return (
    <Card
      data-testid={`${testIdPrefix}-business-days`}
      data-mode={
        compact && confirmed ? 'editing' : confirmed ? 'confirmed' : 'ask'
      }
      data-quiet={quiet ? 'true' : 'false'}
      $quiet={quiet}
      $embedded={embedded}
    >
      <Title>
        {quiet ? 'Streak settings' : 'Which days does this kitchen order?'}
      </Title>
      <Body>{bodyCopy}</Body>
      {suggestionCopy && !confirmed ? (
        <Suggestion
          $quiet={quiet}
          data-testid={`${testIdPrefix}-business-days-suggestion`}
        >
          {suggestionCopy}
        </Suggestion>
      ) : null}
      {confirmed && !quiet ? (
        <Suggestion data-testid={`${testIdPrefix}-business-days-confirmed`}>
          Streaks count on these days.
        </Suggestion>
      ) : null}
      <Days data-testid={`${testIdPrefix}-business-days-chips`}>
        {SUCCESS_HUB_ISO_WEEKDAYS.map(day => {
          const selected = selectedDays.includes(day)
          return (
            <DayButton
              key={day}
              type={quiet ? 'default' : selected ? 'primary' : 'default'}
              $quietSelected={quiet && selected}
              onClick={() => toggleDay(day)}
              disabled={busy}
              data-testid={`${testIdPrefix}-weekday-${day}`}
              aria-pressed={selected}
            >
              {SUCCESS_HUB_WEEKDAY_SHORT[day]}
            </DayButton>
          )
        })}
      </Days>
      <Actions>
        {showUseSuggestion ? (
          quiet ? (
            <QuietLink
              type='link'
              onClick={() => setSelectedDays(suggested)}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-use-suggestion`}
              data-chrome='quiet'
            >
              {useSuggestionLabel}
            </QuietLink>
          ) : (
            <Button
              type='link'
              onClick={() => setSelectedDays(suggested)}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-use-suggestion`}
            >
              {useSuggestionLabel}
            </Button>
          )
        ) : null}
        <ShareWithGroupButton
          offer={{ kind: 'businessDays', venueId }}
          label='Share days with group…'
          component={quiet ? QuietLink : undefined}
          data-chrome={quiet ? 'quiet' : undefined}
          data-testid={`${testIdPrefix}-business-days-share`}
        />
        {canConfirmSelected ? (
          <Button
            type={quiet ? 'default' : 'primary'}
            onClick={() => saveDays(selectedDays)}
            loading={busy}
            disabled={busy}
            data-testid={`${testIdPrefix}-business-days-confirm`}
          >
            {confirmed ? 'Save days' : 'Confirm these days'}
          </Button>
        ) : null}
        {confirmed ? (
          quiet ? (
            <QuietLink
              type='link'
              onClick={() => saveDays([])}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-clear`}
              data-chrome='quiet'
            >
              Stop counting streaks
            </QuietLink>
          ) : (
            <Button
              type='link'
              onClick={() => saveDays([])}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-clear`}
            >
              Stop counting streaks
            </Button>
          )
        ) : null}
        {compact && editing ? (
          quiet ? (
            <QuietLink
              type='link'
              onClick={cancelEdit}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-cancel`}
              data-chrome='quiet'
            >
              Cancel
            </QuietLink>
          ) : (
            <Button
              type='link'
              onClick={cancelEdit}
              disabled={busy}
              data-testid={`${testIdPrefix}-business-days-cancel`}
            >
              Cancel
            </Button>
          )
        ) : null}
      </Actions>
    </Card>
  )
}

const Card = styled.section`
  background: ${({ theme, $embedded }) =>
    $embedded ? 'transparent' : theme?.colours?.mayoWhite || '#fff'};
  border: ${({ $embedded, $quiet, theme }) =>
    $embedded
      ? 'none'
      : $quiet
      ? `1px solid ${theme?.colours?.chipGrey || 'rgba(0, 0, 0, 0.06)'}`
      : `1px solid ${theme?.colours?.borderGrey || '#e8e8e8'}`};
  border-left: ${({ $quiet, $embedded, theme }) =>
    $quiet || $embedded
      ? undefined
      : `4px solid ${theme?.colours?.lettuceGreen || '#7cb305'}`};
  border-radius: ${({ $embedded }) => ($embedded ? '0' : '8px')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ $embedded }) => ($embedded ? '4px 0 0' : '12px 16px')};
  width: 100%;
`

const Summary = styled.section`
  align-items: center;
  background: ${({ theme, $embedded }) =>
    $embedded ? 'transparent' : theme?.colours?.mayoWhite || '#fff'};
  border: ${({ $embedded, theme }) =>
    $embedded
      ? 'none'
      : `1px solid ${theme?.colours?.chipGrey || 'rgba(0, 0, 0, 0.06)'}`};
  border-radius: ${({ $embedded }) => ($embedded ? '0' : '12px')};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: ${({ $embedded }) => ($embedded ? '2px 0 0' : '12px 14px')};
  width: 100%;
`

const SettingsLabel = styled.span`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#8c8c8c'};
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin-bottom: 2px;
`

const SummaryCopy = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  cursor: help;
  flex: 1 1 auto;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const Title = styled.h4`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  margin: 0;
`

const Body = styled(BodyText)`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`

const Suggestion = styled.p`
  color: ${({ theme, $quiet }) =>
    $quiet
      ? theme?.colours?.oliveGrey || '#595959'
      : theme?.colours?.seaweedGreen || '#132d07'};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

/** Progress chrome: olive links — never antd primary / lime vs today’s job CTA. */
const QuietLink = styled(Button)`
  && {
    color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
    font-size: 0.8125rem;
    font-weight: 600;
    height: auto;
    padding: 0;
  }

  &&:hover,
  &&:focus {
    color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  }
`

const Days = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const DayButton = styled(Button)`
  min-width: 48px;
  ${({ $quietSelected, theme }) =>
    $quietSelected
      ? `
    border-color: ${theme?.colours?.bbqBlack || '#262626'} !important;
    color: ${theme?.colours?.bbqBlack || '#262626'} !important;
    font-weight: 700;
  `
      : ''}
`

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export default SuccessHubBusinessDaysCard
